import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, Container } from '@mui/material';

import { Order } from '@luxuryescapes/contract-svc-order';
import * as libRegions from '@luxuryescapes/lib-regions';

import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';
import { getCurrentSubscriptionPeriod, hasLuxPlusSubscriptionStatus } from '~/components/Membership/utils';

import { LUX_PLUS } from '~/consts/membership';

import { useGetLanguageOptions } from '~/hooks/apis/lab/useGetLanguageOptions';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import * as LuxLoyaltyService from '~/services/LuxLoyaltyService';
import { getCustomerMembershipSubscriptions } from '~/services/MembershipService';
import { getOrderDetail, getOrderItemBySubscriptionPeriodId } from '~/services/OrdersService';
import UsersService from '~/services/UsersService';

import { isAdmin } from '~/utils/adminPermission';

import ErrorDisplay from '../Common/ErrorDisplay';
import Spinner from '../Common/Spinner';

import UserDetails from './UserDetails';

const regions = libRegions.getRegions();

export default function UserPage() {
  const tenant = useSelector((state: App.State) => state.tenant);
  const { user: currentUser } = useCurrentUser();
  const { brand } = tenant;
  const { user_email: userIdOrEmail } = useParams<'user_email'>();
  const location = useLocation();
  const { push } = useAppNavigation();
  const returnState = location.state as { fromAdminUsers?: boolean; searchQuerySubmit?: string } | null;

  const backToAdminUsers = () => {
    const searchQuerySubmit = returnState?.searchQuerySubmit ?? '';
    push('/system/admin-users', { searchQuerySubmit, searchString: searchQuerySubmit });
  };

  const isAdminRole = useMemo(() => isAdmin(currentUser), [currentUser]);

  const [user, setUser] = useState<App.User | null>(null);
  const [schema, setSchema] = useState({});
  const [tenancy, setTenancy] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<App.MembershipSubscriptions | null>(null);
  const [luxLoyaltyAccount, setLuxLoyaltyAccount] = useState<App.LuxLoyaltyAccount | null>(null);
  const [currentSubscriptionOrderItem, setCurrentSubscriptionOrderItem] = useState<Order.SubscriptionItem | null>(null);
  const [currentSubscriptionOrder, setCurrentSubscriptionOrder] = useState<App.Order | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const { data: languageOptions = [] } = useGetLanguageOptions();

  const handleUpdate = useCallback((user: App.User) => {
    setUser(user);
  }, []);

  const updatePreferredAgent = async (preferredAgentId: string) => {
    await UsersService.patch({
      id_account: user.id_member,
      params: `preferred_agent_id=${preferredAgentId}`,
    });

    handleUpdate({
      ...user,
      preferred_agent_id: preferredAgentId,
    });
  };

  const fetchSubscriptions = useCallback(
    async (userId: string): Promise<App.MembershipSubscriptions | null> => {
      try {
        const { result } = await getCustomerMembershipSubscriptions({ customerId: userId });
        return result;
      } catch (error) {
        error.status !== 404 &&
          enqueueSnackbar(`There was an error fetching user ${LUX_PLUS.PROGRAM_NAME} subscription`, {
            variant: 'error',
          });
        return null;
      }
    },
    [enqueueSnackbar],
  );

  const fetchCurrentSubscriptionOrderItem = useCallback(
    async (subscriptionPeriodId: string): Promise<Order.SubscriptionItem | null> => {
      try {
        return await getOrderItemBySubscriptionPeriodId(subscriptionPeriodId);
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    [],
  );

  const fetchCustomerLanguagePreference = useCallback(async (userId: string): Promise<string | null> => {
    try {
      return await UsersService.getCustomerLanguagePreference(userId);
    } catch {
      return null;
    }
  }, []);

  const fetchLuxLoyaltyAccount = useCallback(
    async (userId: string): Promise<App.LuxLoyaltyAccount | null> => {
      try {
        if (brand !== 'luxuryescapes') {
          return null;
        }
        return await LuxLoyaltyService.getLuxLoyaltyAccount(userId);
      } catch (error) {
        // 404 means the customer simply isn't a Société member — not an error
        // for the user-detail page; the Société tab handles the enrol path.
        if (error?.status === 404) {
          return null;
        }
        if (isAdmin(currentUser)) {
          // endpoint will only work for admins
          setError(error.message);
        }
        return null;
      }
    },
    [brand, currentUser],
  );

  const fetchOrder = useCallback(async (orderId: string): Promise<App.Order | null> => {
    try {
      return await getOrderDetail(orderId);
    } catch (error) {
      setError(error.message);
      return null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const user: App.User = await UsersService.getUser(userIdOrEmail, { brand });

      const [language, schema, tenancy, subscriptions, luxLoyaltyAccount] = await Promise.all([
        fetchCustomerLanguagePreference(user.id_member),
        UsersService.getUpdateSchema({ id_member: user.id_member }, brand),
        UsersService.getUserTenancy(user.email),
        fetchSubscriptions(user.id_member),
        fetchLuxLoyaltyAccount(user.id_member),
      ]);

      let currentSubscriptionOrderItem = null;
      let currentSubscriptionOrder = null;

      if (hasLuxPlusSubscriptionStatus(subscriptions)) {
        const currentSubscriptionPeriod = getCurrentSubscriptionPeriod(subscriptions);
        if (currentSubscriptionPeriod) {
          currentSubscriptionOrderItem = await fetchCurrentSubscriptionOrderItem(currentSubscriptionPeriod.id);
        }
      }

      if (currentSubscriptionOrderItem) {
        currentSubscriptionOrder = await fetchOrder(currentSubscriptionOrderItem.fk_order_id);
      }

      setUser({ ...user, language });
      setSchema(schema);
      setTenancy(tenancy);
      setSubscriptions(subscriptions);
      setLuxLoyaltyAccount(luxLoyaltyAccount);
      setCurrentSubscriptionOrderItem(currentSubscriptionOrderItem);
      setCurrentSubscriptionOrder(currentSubscriptionOrder);
    } catch (e) {
      //setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [
    brand,
    fetchCurrentSubscriptionOrderItem,
    fetchCustomerLanguagePreference,
    fetchLuxLoyaltyAccount,
    fetchOrder,
    fetchSubscriptions,
    userIdOrEmail,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Container>
      {returnState?.fromAdminUsers && (
        <Button startIcon={<ChevronLeftIcon />} onClick={backToAdminUsers}>
          Back to Admin Users &amp; Roles
        </Button>
      )}
      <UserBreadcrumbs
        userId={user?.id_member || userIdOrEmail}
        user={
          user
            ? { label: user.fullName || user.email || 'User', href: `/users/${user.id_member || userIdOrEmail}` }
            : null
        }
      />
      {isLoading && <Spinner />}

      {error && <ErrorDisplay message={error} />}

      {!isLoading && user && (
        <UserDetails
          user={user}
          isAdmin={isAdminRole}
          schema={schema}
          tenant={tenant}
          tenancy={tenancy}
          regions={regions}
          update={handleUpdate}
          updatePreferredAgent={updatePreferredAgent}
          subscriptions={subscriptions}
          luxLoyaltyAccount={luxLoyaltyAccount}
          languageOptions={languageOptions}
          currentSubscriptionOrderItem={currentSubscriptionOrderItem}
          currentSubscriptionOrder={currentSubscriptionOrder}
          refreshData={fetchData}
        />
      )}
    </Container>
  );
}
